import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X, Check, ExternalLink, Pencil } from 'lucide-react';
import { Admin, type CrmTemplate, type CrmTemplateInput } from '@/lib/api';
import clsx from 'clsx';

export default function AdminCrmTemplates() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; tpl: CrmTemplate } | null>(null);

  const list = useQuery({ queryKey: ['admin-crm-templates'], queryFn: Admin.listGlobalCrmTemplates });
  const remove = useMutation({
    mutationFn: (id: number) => Admin.removeGlobalCrmTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-crm-templates'] }),
  });

  return (
    <div className="p-5 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ExternalLink className="text-blue-600" size={22} /> CRM Şablonları
          </h1>
          <p className="text-sm text-slate-500">
            Tüm agent'ların softphone'unda otomatik tetiklenecek CRM popup URL'leri.
            Placeholder: <code className="bg-slate-100 px-1 rounded">{'{{caller}}'}</code>,
            <code className="bg-slate-100 px-1 rounded ml-1">{'{{ext}}'}</code>,
            <code className="bg-slate-100 px-1 rounded ml-1">{'{{call_id}}'}</code>,
            <code className="bg-slate-100 px-1 rounded ml-1">{'{{direction}}'}</code>
          </p>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
        >
          <Plus size={16} /> Yeni Şablon
        </button>
      </header>

      <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
        {list.isLoading && <div className="p-6 text-slate-500">Yükleniyor…</div>}
        {list.data && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Ad</th>
                <th className="text-left px-3 py-2">URL Şablonu</th>
                <th className="text-left px-3 py-2">Açma Modu</th>
                <th className="text-left px-3 py-2">Browser</th>
                <th className="text-left px-3 py-2">Tetik</th>
                <th className="text-center px-3 py-2">Varsayılan</th>
                <th className="text-center px-3 py-2">Aktif</th>
                <th className="text-right px-3 py-2">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((tpl) => (
                <tr key={tpl.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{tpl.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600 max-w-xs truncate" title={tpl.url_template}>
                    {tpl.url_template}
                  </td>
                  <td className="px-3 py-2"><Pill>{tpl.open_mode}</Pill></td>
                  <td className="px-3 py-2"><Pill>{tpl.desktop_browser}</Pill></td>
                  <td className="px-3 py-2"><Pill>{tpl.trigger_on}</Pill></td>
                  <td className="px-3 py-2 text-center">
                    {tpl.is_default ? <Check size={16} className="text-emerald-600 inline" /> : null}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {tpl.is_active ? <Check size={16} className="text-emerald-600 inline" /> : <X size={16} className="text-red-500 inline" />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setModal({ mode: 'edit', tpl })} className="text-slate-500 hover:text-blue-600 p-1">
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => { if (confirm(`'${tpl.name}' silinsin mi?`)) remove.mutate(tpl.id); }}
                      className="text-slate-500 hover:text-red-600 p-1 ml-1"
                    ><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {list.data.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Henüz şablon yok. "Yeni Şablon" ile başla.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && <Modal mode={modal.mode} tpl={modal.mode === 'edit' ? modal.tpl : undefined} onClose={() => setModal(null)} />}
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono">{children}</span>;
}

interface ModalProps { mode: 'create' | 'edit'; tpl?: CrmTemplate; onClose: () => void; }

function Modal({ mode, tpl, onClose }: ModalProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CrmTemplateInput>({
    name: tpl?.name ?? '',
    url_template: tpl?.url_template ?? '',
    open_mode: tpl?.open_mode ?? 'named',
    desktop_browser: tpl?.desktop_browser ?? 'default',
    trigger_on: tpl?.trigger_on ?? 'answered',
    is_default: tpl?.is_default ?? false,
    is_active: tpl?.is_active ?? true,
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => mode === 'create'
      ? Admin.createGlobalCrmTemplate(form)
      : Admin.updateGlobalCrmTemplate(tpl!.id, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-crm-templates'] });
      onClose();
    },
    onError: (e: any) => setErr(e?.response?.data?.message ?? e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-xl w-[560px] max-h-[90vh] flex flex-col">
        <header className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold">{mode === 'create' ? 'Yeni CRM Şablonu' : `Düzenle: ${tpl?.name}`}</h2>
          <button onClick={onClose} className="text-slate-500"><X size={18} /></button>
        </header>
        <div className="p-5 space-y-3 overflow-y-auto">
          <Field label="Ad">
            <input
              autoFocus value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-2.5 py-1.5 border rounded text-sm"
              placeholder="Müşteri Kartı"
            />
          </Field>

          <Field
            label="URL Şablonu"
            hint="Mustache: {{caller}} {{callee}} {{ext}} {{call_id}} {{direction}}  ·  3CX/Arkbase: %CallerNumber% %CalledNumber% %Extension% %CallID% %Direction%"
          >
            <input
              value={form.url_template}
              onChange={(e) => setForm({ ...form, url_template: e.target.value })}
              className="w-full px-2.5 py-1.5 border rounded text-sm font-mono"
              placeholder="http://10.10.20.20:3000/cti-popup?phone=%CallerNumber%"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Açma Modu">
              <select
                value={form.open_mode}
                onChange={(e) => setForm({ ...form, open_mode: e.target.value as any })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="named">named (aynı tab güncellenir)</option>
                <option value="new_tab">new_tab (yeni tab)</option>
                <option value="popup">popup (yeni pencere)</option>
                <option value="embedded">embedded (iframe)</option>
              </select>
            </Field>
            <Field label="Browser (Desktop)">
              <select
                value={form.desktop_browser}
                onChange={(e) => setForm({ ...form, desktop_browser: e.target.value as any })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="default">Default (sistem)</option>
                <option value="chrome">Chrome</option>
                <option value="firefox">Firefox</option>
                <option value="edge">Edge</option>
              </select>
            </Field>
            <Field label="Tetikleme">
              <select
                value={form.trigger_on}
                onChange={(e) => setForm({ ...form, trigger_on: e.target.value as any })}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="answered">Cevaplandı</option>
                <option value="ring">Çağrı geldi</option>
                <option value="ended">Çağrı bitti</option>
              </select>
            </Field>
          </div>

          <div className="flex gap-4 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_default ?? false}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              />
              Varsayılan (otomatik tetiklenir)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Aktif
            </label>
          </div>

          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-slate-100 rounded">İptal</button>
          <button
            onClick={() => save.mutate()}
            disabled={!form.name.trim() || !form.url_template.trim() || save.isPending}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm rounded"
          >{save.isPending ? 'Kaydediliyor…' : 'Kaydet'}</button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}
